import React, {ReactNode} from 'react';
import {t} from '@lingui/core/macro';

import {AppUrls} from 'shared/config/model/urls';

import ListPage from 'shared/ui/ListPage/ListPage';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import {IColumn} from 'shared/ui/Table/Table';
import IPagination from 'shared/api/model/IPagination';
import {RowFactory} from 'shared/ui/Table/tableHelper';
import ListFiltersModal, {IFiltersFormComponentProps} from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import useOpenState from 'shared/lib/hooks/useOpenState';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';

import {IListParams} from 'shared/lib/hooks/useListParams';

export interface IAdminListQuery<T> {
    data?: IPagination<T>,
    isLoading: boolean,
    isFetching: boolean,
    refetch: () => void,
}

interface IAdminListScreenProps<T extends object> {
    title: string,
    heading: string,
    createButton?: ReactNode,
    columns: IColumn[],
    params: Pick<IListParams, 'sort' | 'setSort' | 'search' | 'onSearch' | 'hasFilters'>,
    query: IAdminListQuery<T>,
    rowFactory: RowFactory<T>,
    hideCreateButtonOnMobile?: boolean,
    FiltersForm?: React.ComponentType<IFiltersFormComponentProps>,
}

const AdminListScreen = <T extends object>({
    title,
    heading,
    createButton,
    columns,
    params,
    query,
    rowFactory,
    hideCreateButtonOnMobile,
    FiltersForm,
}: IAdminListScreenProps<T>) => {
    const {data: pagination, isLoading, isFetching, refetch} = query;
    const filtersModal = useOpenState();
    const isRedirecting = useResetPageOnEmpty(pagination);

    return (
        <ListPage
            documentTitle={`${title} | ${t`Overseer`}`}
            toolbar={
                <ListToolbar
                    title={heading}
                    backUrl={AppUrls.SETTINGS}
                    createButton={createButton}
                    hideCreateButtonOnMobile={hideCreateButtonOnMobile}
                    actions={
                        <TableToolbar
                            onSearch={params.onSearch}
                            searchValue={params.search}
                            onFilter={FiltersForm && filtersModal.open}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                        />
                    }
                />
            }
            emptyStateComponent={params.hasFilters ? t`Ничего не найдено` : undefined}
            columns={columns}
            items={pagination?.items}
            isLoading={isLoading || isRedirecting}
            rowFactory={rowFactory}
            sort={params.sort}
            setSort={params.setSort}
            pagination={pagination}
        >
            {FiltersForm && (
                <ListFiltersModal open={filtersModal.isOpen} handleClose={filtersModal.close} Form={FiltersForm}/>
            )}
        </ListPage>
    );
};

export default AdminListScreen;
